/**
 * DecisionFlow — TrueUpManager
 * data-fit LLC · Confidential
 * Domain: P3-05 Retroactive True-Up
 * Tier: Enterprise — feature_gate_key = 'feature.tier.true_up'
 * Spec authority: decisionflow_phase3_plan_spec_v1_0.md §6
 * API Contract: api_contract_v1_32.md §51
 * Wizard substages:
 *   true_up_review · true_up_approval
 *
 * Flow:
 *   List adjustments → create draft → submit → approve / reject → engine applies
 *   Source payout context panel fetched per adjustment for reviewer context
 *
 * DDL ground truth (phase3_plan_spec §6.2):
 *   compensation_adjustment:
 *     adjustment_id uuid PK, tenant_id uuid,
 *     position_id uuid FK, source_payout_summary_id uuid FK,
 *     source_fiscal_period_id int, current_fiscal_period_id int,
 *     adjustment_type varchar(30) CHECK(quota_correction|credit_correction|rate_correction|fx_correction|other),
 *     adjustment_reason text NN, calculated_delta numeric(18,4) NN,
 *     adjustment_currency char(3) NN,
 *     adjustment_status varchar(20) CHECK(draft|pending_approval|approved|applied|rejected) — AUTHORITATIVE
 *     submitted_by uuid, submitted_at timestamptz,
 *     approved_by uuid, approved_at timestamptz,
 *     rejected_by uuid, rejected_at timestamptz, rejection_reason text,
 *     applied_in_run_id uuid, applied_at timestamptz,
 *     created_by uuid, created_at timestamptz, updated_at timestamptz
 *
 * IMPORTANT — adjustment_status is the authoritative state column (unlike Phase 2 plans
 * where state is derived from approved_by). Use adjustment_status directly.
 *
 * API surface (api_contract_v1_32.md §51):
 *   POST /api/v1/compensation-adjustments
 *   GET  /api/v1/compensation-adjustments?position_id={id}&fiscal_period_id={id}&adjustment_status={s}
 *   GET  /api/v1/compensation-adjustments/{adjustment_id}
 *   PUT  /api/v1/compensation-adjustments/{adjustment_id}        — draft only, raw fetch
 *   POST /api/v1/compensation-adjustments/{adjustment_id}/submit
 *   POST /api/v1/compensation-adjustments/{adjustment_id}/approve
 *   POST /api/v1/compensation-adjustments/{adjustment_id}/reject — body: { rejection_reason }
 *   GET  /api/v1/compensation-adjustments/{adjustment_id}/source-payout
 *
 * Architecture rules:
 *   - useAuth() for { user } — no token in props (ARCH-2)
 *   - apiGet / apiPost from client — PUT via raw fetch
 *   - tenant_id never in any request body
 *   - No window.prompt / window.confirm — inline rejection reason state
 *   - No ApprovalPanel — 5-state machine (draft/pending_approval/approved/applied/rejected)
 *     with reject pathway requires custom workflow UI
 *   - 403 FEATURE_UNAVAILABLE → GateStatusBanner (Enterprise gate)
 *
 * Props:
 *   mode             — 'wizard' | 'admin'
 *   period           — { fiscal_period_id, period_code } | null
 *   wizardSteps      — array (wizard context)
 *   embedded         — bool  — reserved; these managers are tenant-scoped
 *                              (COMPENSATION POLICIES nav, not PlanDetailView)
 *   readOnly         — bool  — true when viewing archived context
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Constants — DDL-authoritative
// ---------------------------------------------------------------------------

const ADJUSTMENT_TYPES = [
  { value: 'quota_correction',  label: 'Quota correction',  hint: 'Quota was loaded at wrong amount' },
  { value: 'credit_correction', label: 'Credit correction', hint: 'Credit was missing or incorrect' },
  { value: 'rate_correction',   label: 'Rate correction',   hint: 'Commission rate was incorrect' },
  { value: 'fx_correction',     label: 'FX correction',     hint: 'FX rate was corrected after period close' },
  { value: 'other',             label: 'Other',             hint: 'Explain in adjustment reason' },
];

const ADJUSTMENT_STATUSES = [
  { value: '',                label: 'All statuses' },
  { value: 'draft',           label: 'Draft' },
  { value: 'pending_approval', label: 'Pending approval' },
  { value: 'approved',        label: 'Approved' },
  { value: 'applied',         label: 'Applied' },
  { value: 'rejected',        label: 'Rejected' },
];

const CURRENCY_CODES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'SGD', 'JPY', 'CNY'];

// Status → display metadata
const STATUS_META = {
  draft:            { color: 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]',     label: 'Draft' },
  pending_approval: { color: 'bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))]',           label: 'Pending approval' },
  approved:         { color: 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]',           label: 'Approved' },
  applied:          { color: 'bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))]',           label: 'Applied' },
  rejected:         { color: 'bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]',             label: 'Rejected' },
};

const EMPTY_ADJ_FORM = {
  position_id:             '',
  source_payout_summary_id: '',
  current_fiscal_period_id: '',
  adjustment_type:         'quota_correction',
  adjustment_reason:       '',
  calculated_delta:        '',
  adjustment_currency:     'USD',
};

// ---------------------------------------------------------------------------
// Shared CSS class strings — token-compliant
// ---------------------------------------------------------------------------

const thClass =
  'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide ' +
  'text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] ' +
  'border-b border-[rgb(var(--color-border))]';

const tdClass =
  'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] ' +
  'border-b border-[rgb(var(--color-border))]';

const btnPrimary =
  'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium ' +
  'text-white hover:bg-[rgb(var(--color-primary-hover))] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const btnSecondary =
  'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] ' +
  'px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] ' +
  'hover:bg-[rgb(var(--color-surface-muted))] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const btnDanger =
  'rounded-lg border border-[rgb(var(--color-danger))]/40 px-3 py-1.5 text-tok-sm ' +
  'font-medium text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-danger))]/10 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const btnSuccess =
  'rounded-lg bg-[rgb(var(--color-success))] px-3 py-1.5 text-tok-sm font-medium ' +
  'text-white hover:opacity-90 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const inputClass =
  'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] ' +
  'bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] ' +
  'px-3 py-2 text-tok-sm focus:outline-none ' +
  'focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';

const labelClass = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getErrorMessage(error, fallback) {
  return error?.message || error?.detail || fallback;
}

function getApiErrorCode(error) {
  return error?.code || null;
}

function fmtDate(val) {
  if (!val) return '—';
  return String(val).slice(0, 10);
}

function fmtMoney(val, currency) {
  if (val === null || val === undefined || val === '') return '—';
  const n = Number(val);
  const formatted = Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const sign = n < 0 ? '−' : n > 0 ? '+' : '';
  return `${sign}${formatted}${currency ? ` ${currency}` : ''}`;
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.draft;
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-tok-xs font-medium ${meta.color}`}>
      {meta.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: SourcePayoutPanel — fetches and displays source payout context
// ---------------------------------------------------------------------------

function SourcePayoutPanel({ adjustmentId, onClose }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (!adjustmentId) return;
    setLoading(true);
    setError('');
    apiGet(`/api/v1/compensation-adjustments/${adjustmentId}/source-payout`)
      .then((d) => setData(d))
      .catch((e) => setError(getErrorMessage(e, 'Unable to load source payout context.')))
      .finally(() => setLoading(false));
  }, [adjustmentId]);

  return (
    <div className="rounded-xl bg-[rgb(var(--color-surface-muted))]/60 border border-[rgb(var(--color-border))] p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h4 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
          Source period payout context
        </h4>
        <button type="button" className={btnSecondary} onClick={onClose}>Hide</button>
      </div>
      <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
        Original payout from the prior period being adjusted. This record is immutable — the true-up
        writes a new row in the current period only.
      </p>

      {loading && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading source payout…</p>}

      {error && (
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {error}
        </div>
      )}

      {data && !loading && (
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-tok-sm">
          <div>
            <dt className="text-[rgb(var(--color-text-muted))] font-medium">Period</dt>
            <dd className="text-[rgb(var(--color-text))]">{data.period_code || data.source_fiscal_period_id || '—'}</dd>
          </div>
          <div>
            <dt className="text-[rgb(var(--color-text-muted))] font-medium">Run type</dt>
            <dd className="text-[rgb(var(--color-text))]">{data.run_type || '—'}</dd>
          </div>
          <div>
            <dt className="text-[rgb(var(--color-text-muted))] font-medium">Total payout</dt>
            <dd className="text-[rgb(var(--color-text))] font-medium">
              {data.total_payout != null
                ? `${Number(data.total_payout).toLocaleString(undefined, { minimumFractionDigits: 2 })} ${data.payout_currency_code || ''}`
                : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-[rgb(var(--color-text-muted))] font-medium">Attainment</dt>
            <dd className="text-[rgb(var(--color-text))]">
              {data.attainment_pct != null
                ? `${(Number(data.attainment_pct) * 100).toFixed(2)}%`
                : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-[rgb(var(--color-text-muted))] font-medium">ARR payout</dt>
            <dd className="text-[rgb(var(--color-text))]">
              {data.arr_payout != null
                ? Number(data.arr_payout).toLocaleString(undefined, { minimumFractionDigits: 2 })
                : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-[rgb(var(--color-text-muted))] font-medium">Quota</dt>
            <dd className="text-[rgb(var(--color-text))]">
              {data.quota_reporting != null
                ? Number(data.quota_reporting).toLocaleString(undefined, { minimumFractionDigits: 2 })
                : '—'}
            </dd>
          </div>
        </dl>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function TrueUpManager({ mode, period, wizardSteps, embedded = false, readOnly = false }) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.true_up')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }

  const [featureUnavailable, setFeatureUnavailable] = useState(false);
  const [toast, setToast]                           = useState(null);

  // List state
  const [adjustments, setAdjustments]               = useState([]);
  const [listLoading, setListLoading]               = useState(false);
  const [listError, setListError]                   = useState('');
  const [filterStatus, setFilterStatus]             = useState('');
  const [filterPositionId, setFilterPositionId]     = useState('');

  // Positions (for create form selector)
  const [positions, setPositions]                   = useState([]);

  // Selected adjustment detail
  const [selectedAdj, setSelectedAdj]               = useState(null);
  const [detailLoading, setDetailLoading]           = useState(false);
  const [actionLoading, setActionLoading]           = useState(false);
  const [actionError, setActionError]               = useState('');
  const [showSourcePayout, setShowSourcePayout]     = useState(false);

  // Rejection flow — inline state
  const [showRejectForm, setShowRejectForm]         = useState(false);
  const [rejectionReason, setRejectionReason]       = useState('');
  const [rejectionError, setRejectionError]         = useState('');

  // Create/edit form
  const [showAdjForm, setShowAdjForm]               = useState(false);
  const [adjForm, setAdjForm]                       = useState(EMPTY_ADJ_FORM);
  const [adjSaving, setAdjSaving]                   = useState(false);
  const [adjFormError, setAdjFormError]             = useState('');
  const [editingAdjId, setEditingAdjId]             = useState(null);

  // Source payout lookup in create form
  const [sourceLookupId, setSourceLookupId]         = useState('');
  const [sourceLookupData, setSourceLookupData]     = useState(null);
  const [sourceLookupLoading, setSourceLookupLoading] = useState(false);
  const [sourceLookupError, setSourceLookupError]   = useState('');

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const currentUserId = user?.user_id || user?.id || null;

  // ---------------------------------------------------------------------------
  // Load adjustments list
  // ---------------------------------------------------------------------------

  const loadAdjustments = useCallback(async () => {
    if (!user) return;
    setListLoading(true);
    setListError('');
    try {
      const params = new URLSearchParams();
      const periodId = period?.fiscal_period_id;
      if (periodId)           params.set('fiscal_period_id', String(periodId));
      if (filterStatus)       params.set('adjustment_status', filterStatus);
      if (filterPositionId.trim()) params.set('position_id', filterPositionId.trim());
      const url = `/api/v1/compensation-adjustments${params.toString() ? `?${params}` : ''}`;
      const data = await apiGet(url);
      setAdjustments(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      if (getApiErrorCode(error) === 'FEATURE_UNAVAILABLE' || error?.status === 403) {
        setFeatureUnavailable(true);
        return;
      }
      setListError(getErrorMessage(error, 'Unable to load adjustments.'));
    } finally {
      setListLoading(false);
    }
  }, [user, period, filterStatus, filterPositionId]);

  const loadPositions = useCallback(async () => {
    if (!user) return;
    try {
      const data = await apiGet('/api/v1/positions');
      setPositions(Array.isArray(data) ? data : data?.positions || data?.data || []);
    } catch { /* non-blocking */ }
  }, [user]);

  useEffect(() => { loadAdjustments(); loadPositions(); }, [loadAdjustments, loadPositions]);

  // ---------------------------------------------------------------------------
  // Load adjustment detail
  // ---------------------------------------------------------------------------

  const loadAdjDetail = useCallback(async (adjId) => {
    setDetailLoading(true);
    setActionError('');
    setShowSourcePayout(false);
    setShowRejectForm(false);
    setRejectionReason('');
    setRejectionError('');
    try {
      const data = await apiGet(`/api/v1/compensation-adjustments/${adjId}`);
      setSelectedAdj(data);
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to load adjustment detail.'));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Source payout lookup (used in create form to pre-populate context)
  // ---------------------------------------------------------------------------

  const lookupSourcePayout = useCallback(async () => {
    if (!sourceLookupId.trim()) { setSourceLookupError('Enter a payout summary ID to look up.'); return; }
    setSourceLookupLoading(true);
    setSourceLookupError('');
    setSourceLookupData(null);
    try {
      // We temporarily create-and-discard is not right — instead use a search-like pattern.
      // The /source-payout endpoint requires an adjustment_id, not a payout_summary_id directly.
      // Use the payout-summaries endpoint instead for pre-flight context.
      const data = await apiGet(`/api/v1/payout-summaries/${sourceLookupId.trim()}`);
      setSourceLookupData(data);
      setAdjForm((prev) => ({
        ...prev,
        source_payout_summary_id: sourceLookupId.trim(),
        adjustment_currency: data?.payout_currency_code || prev.adjustment_currency,
      }));
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'TRUE_UP_SOURCE_NOT_FOUND' || error?.status === 404)
        setSourceLookupError('Payout summary not found. Verify the ID belongs to a prior-period final run.');
      else
        setSourceLookupError(getErrorMessage(error, 'Unable to look up payout summary.'));
    } finally {
      setSourceLookupLoading(false);
    }
  }, [sourceLookupId]);

  // ---------------------------------------------------------------------------
  // Create adjustment
  // ---------------------------------------------------------------------------

  const updateAdjForm = useCallback((field, value) =>
    setAdjForm((prev) => ({ ...prev, [field]: value })), []);

  const resetAdjForm = useCallback(() => {
    setAdjForm(EMPTY_ADJ_FORM);
    setAdjFormError('');
    setEditingAdjId(null);
    setSourceLookupId('');
    setSourceLookupData(null);
    setSourceLookupError('');
  }, []);

  const validateAdjForm = useCallback(() => {
    if (!adjForm.position_id)              return 'Position is required.';
    if (!adjForm.source_payout_summary_id.trim()) return 'Source payout summary ID is required.';
    if (!adjForm.current_fiscal_period_id) return 'Current fiscal period ID is required.';
    if (!adjForm.adjustment_type)          return 'Adjustment type is required.';
    if (!adjForm.adjustment_reason.trim()) return 'Adjustment reason is required — explain why this correction is needed.';
    if (!adjForm.calculated_delta || adjForm.calculated_delta === '0') return 'Delta amount must be non-zero.';
    if (isNaN(Number(adjForm.calculated_delta))) return 'Delta must be a valid number.';
    if (!adjForm.adjustment_currency)      return 'Currency is required.';
    return '';
  }, [adjForm]);

  const createAdjustment = useCallback(async () => {
    const err = validateAdjForm();
    if (err) { setAdjFormError(err); return; }
    setAdjSaving(true);
    setAdjFormError('');
    try {
      const payload = {
        position_id:              adjForm.position_id,
        source_payout_summary_id: adjForm.source_payout_summary_id.trim(),
        current_fiscal_period_id: Number(adjForm.current_fiscal_period_id),
        adjustment_type:          adjForm.adjustment_type,
        adjustment_reason:        adjForm.adjustment_reason.trim(),
        calculated_delta:         adjForm.calculated_delta,
        adjustment_currency:      adjForm.adjustment_currency,
      };
      const created = await apiPost('/api/v1/compensation-adjustments', payload);
      await loadAdjustments();
      setShowAdjForm(false);
      resetAdjForm();
      showToast('Adjustment created.');
      const newId = created?.adjustment_id;
      if (newId) await loadAdjDetail(newId);
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'TRUE_UP_SAME_PERIOD')
        setAdjFormError('The source period and current period are the same. True-up must reference a different (prior) period.');
      else if (code === 'TRUE_UP_SOURCE_NOT_FOUND')
        setAdjFormError('Source payout summary not found. Verify the ID belongs to a completed final run in a prior period.');
      else if (code === 'TRUE_UP_ZERO_DELTA')
        setAdjFormError('Delta cannot be zero. Enter the correction amount.');
      else
        setAdjFormError(getErrorMessage(error, 'Unable to create adjustment.'));
    } finally {
      setAdjSaving(false);
    }
  }, [adjForm, loadAdjDetail, loadAdjustments, resetAdjForm, showToast, validateAdjForm]);

  // ---------------------------------------------------------------------------
  // Update adjustment (PUT — draft only, raw fetch)
  // ---------------------------------------------------------------------------

  const updateAdjustment = useCallback(async () => {
    if (!editingAdjId) return;
    const err = validateAdjForm();
    if (err) { setAdjFormError(err); return; }
    setAdjSaving(true);
    setAdjFormError('');
    try {
      const res = await fetch(`/api/v1/compensation-adjustments/${editingAdjId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          adjustment_type:     adjForm.adjustment_type,
          adjustment_reason:   adjForm.adjustment_reason.trim(),
          calculated_delta:    adjForm.calculated_delta,
          adjustment_currency: adjForm.adjustment_currency,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const code = body?.error?.code || body?.code;
        if (code === 'INVALID_STATUS_TRANSITION')
          throw new Error('Only draft adjustments can be edited.');
        if (code === 'TRUE_UP_ZERO_DELTA')
          throw new Error('Delta cannot be zero.');
        throw new Error(body?.error?.message || body?.detail || `Update failed (${res.status})`);
      }
      await loadAdjDetail(editingAdjId);
      await loadAdjustments();
      setShowAdjForm(false);
      resetAdjForm();
      showToast('Adjustment updated.');
    } catch (error) {
      setAdjFormError(error.message || 'Unable to update adjustment.');
    } finally {
      setAdjSaving(false);
    }
  }, [editingAdjId, adjForm, loadAdjDetail, loadAdjustments, resetAdjForm, showToast, validateAdjForm]);

  const openEditAdj = useCallback((adj) => {
    setEditingAdjId(adj.adjustment_id);
    setAdjForm({
      position_id:              adj.position_id || '',
      source_payout_summary_id: adj.source_payout_summary_id || '',
      current_fiscal_period_id: String(adj.current_fiscal_period_id || ''),
      adjustment_type:          adj.adjustment_type || 'quota_correction',
      adjustment_reason:        adj.adjustment_reason || '',
      calculated_delta:         adj.calculated_delta != null ? String(adj.calculated_delta) : '',
      adjustment_currency:      adj.adjustment_currency || 'USD',
    });
    setAdjFormError('');
    setShowAdjForm(true);
  }, []);

  // ---------------------------------------------------------------------------
  // Workflow actions — submit / approve / reject
  // ---------------------------------------------------------------------------

  const submitAdj = useCallback(async () => {
    if (!selectedAdj) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/compensation-adjustments/${selectedAdj.adjustment_id}/submit`, {});
      await loadAdjDetail(selectedAdj.adjustment_id);
      await loadAdjustments();
      showToast('Adjustment submitted for approval.');
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to submit adjustment.'));
    } finally {
      setActionLoading(false);
    }
  }, [loadAdjDetail, loadAdjustments, selectedAdj, showToast]);

  const approveAdj = useCallback(async () => {
    if (!selectedAdj) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/compensation-adjustments/${selectedAdj.adjustment_id}/approve`, {});
      await loadAdjDetail(selectedAdj.adjustment_id);
      await loadAdjustments();
      showToast('Adjustment approved. It will be applied in the next calculation run.');
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'SELF_APPROVAL_BLOCKED')
        setActionError('You submitted this adjustment — a different user must approve it.');
      else if (code === 'TRUE_UP_ALREADY_APPLIED')
        setActionError('This adjustment has already been applied.');
      else
        setActionError(getErrorMessage(error, 'Unable to approve adjustment.'));
    } finally {
      setActionLoading(false);
    }
  }, [loadAdjDetail, loadAdjustments, selectedAdj, showToast]);

  const rejectAdj = useCallback(async () => {
    if (!selectedAdj) return;
    if (!rejectionReason.trim()) { setRejectionError('A rejection reason is required.'); return; }
    setActionLoading(true);
    setRejectionError('');
    setActionError('');
    try {
      await apiPost(`/api/v1/compensation-adjustments/${selectedAdj.adjustment_id}/reject`, {
        rejection_reason: rejectionReason.trim(),
      });
      await loadAdjDetail(selectedAdj.adjustment_id);
      await loadAdjustments();
      setShowRejectForm(false);
      setRejectionReason('');
      showToast('Adjustment rejected.');
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'SELF_APPROVAL_BLOCKED')
        setRejectionError('You submitted this adjustment — a different user must reject it.');
      else
        setRejectionError(getErrorMessage(error, 'Unable to reject adjustment.'));
    } finally {
      setActionLoading(false);
    }
  }, [loadAdjDetail, loadAdjustments, rejectionReason, selectedAdj, showToast]);

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const pendingCount = useMemo(
    () => adjustments.filter((a) => a.adjustment_status === 'pending_approval').length,
    [adjustments]
  );

  const positionNameById = useMemo(
    () => positions.reduce((acc, p) => {
      acc[String(p.position_id)] = p.full_name || `Position ${p.position_id}`;
      return acc;
    }, {}),
    [positions]
  );

  // ---------------------------------------------------------------------------
  // Feature gate / auth guard
  // ---------------------------------------------------------------------------

  if (featureUnavailable) {
    return (
      <GateStatusBanner
        feature="Retroactive True-Up"
        message="Retroactive true-up adjustments require the Enterprise tier (feature.tier.true_up). Contact your account manager to enable this feature."
      />
    );
  }

  if (!user) {
    return (
      <section className="flex flex-col gap-6">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">True-Up Adjustments</h2>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication required.
        </div>
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Adjustment detail view
  // ---------------------------------------------------------------------------

  if (selectedAdj) {
    const status   = selectedAdj.adjustment_status;
    const isDraft  = status === 'draft';
    const isPending = status === 'pending_approval';
    const isApproved = status === 'approved';
    const isTerminal = status === 'applied' || status === 'rejected';
    const isSelfSubmitter = selectedAdj.submitted_by === currentUserId;
    const adjType = ADJUSTMENT_TYPES.find((t) => t.value === selectedAdj.adjustment_type);

    return (
      <section className="flex flex-col gap-6">
        {toast && (
          <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
            {toast}
          </div>
        )}

        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <button type="button" className={btnSecondary}
              onClick={() => {
                setSelectedAdj(null);
                setActionError('');
                setShowAdjForm(false);
                resetAdjForm();
                setShowRejectForm(false);
                setRejectionReason('');
              }}>
              ← Back to adjustments
            </button>
            <h2 className="mt-3 text-tok-xl font-semibold text-[rgb(var(--color-text))]">
              True-Up Adjustment
            </h2>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
              {positionNameById[String(selectedAdj.position_id)] || selectedAdj.position_id} ·
              {adjType?.label || selectedAdj.adjustment_type} ·
              Created {fmtDate(selectedAdj.created_at)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={status} />
            {isDraft && (
              <button type="button" className={btnSecondary} onClick={() => openEditAdj(selectedAdj)}>
                Edit
              </button>
            )}
          </div>
        </header>

        {actionError && (
          <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
            {actionError}
          </div>
        )}

        {/* Adjustment detail */}
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-3 shadow-sm">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Adjustment details</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-tok-sm">
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Position</dt>
              <dd className="text-[rgb(var(--color-text))]">
                {positionNameById[String(selectedAdj.position_id)] || selectedAdj.position_id}
              </dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Adjustment type</dt>
              <dd className="text-[rgb(var(--color-text))]">{adjType?.label || selectedAdj.adjustment_type}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Delta amount</dt>
              <dd className={`font-medium text-tok-base ${
                Number(selectedAdj.calculated_delta) > 0
                  ? 'text-[rgb(var(--color-success))]'
                  : 'text-[rgb(var(--color-danger))]'
              }`}>
                {fmtMoney(selectedAdj.calculated_delta, selectedAdj.adjustment_currency)}
              </dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Current period</dt>
              <dd className="text-[rgb(var(--color-text))]">
                {period?.period_code || `Period ${selectedAdj.current_fiscal_period_id}`}
              </dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Source period</dt>
              <dd className="text-[rgb(var(--color-text))]">Period {selectedAdj.source_fiscal_period_id}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Source payout summary</dt>
              <dd className="text-tok-xs text-[rgb(var(--color-text-muted))] break-all">
                {selectedAdj.source_payout_summary_id}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Reason</dt>
              <dd className="text-[rgb(var(--color-text))] whitespace-pre-wrap">{selectedAdj.adjustment_reason}</dd>
            </div>
            {isTerminal && status === 'rejected' && selectedAdj.rejection_reason && (
              <div className="sm:col-span-2">
                <dt className="text-[rgb(var(--color-danger))] font-medium">Rejection reason</dt>
                <dd className="text-[rgb(var(--color-text))] whitespace-pre-wrap">{selectedAdj.rejection_reason}</dd>
              </div>
            )}
            {status === 'applied' && selectedAdj.applied_in_run_id && (
              <div className="sm:col-span-2">
                <dt className="text-[rgb(var(--color-text-muted))] font-medium">Applied in run</dt>
                <dd className="text-tok-xs text-[rgb(var(--color-text-muted))] break-all">
                  {selectedAdj.applied_in_run_id}
                </dd>
              </div>
            )}
          </dl>

          {/* Source payout toggle */}
          <div className="pt-1">
            <button type="button" className={btnSecondary}
              onClick={() => setShowSourcePayout((v) => !v)}>
              {showSourcePayout ? 'Hide source payout' : 'View source payout context'}
            </button>
          </div>
        </div>

        {/* Source payout panel */}
        {showSourcePayout && (
          <SourcePayoutPanel
            adjustmentId={selectedAdj.adjustment_id}
            onClose={() => setShowSourcePayout(false)}
          />
        )}

        {/* Inline edit form */}
        {showAdjForm && editingAdjId === selectedAdj.adjustment_id && (
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Edit adjustment</h3>
            {adjFormError && (
              <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                {adjFormError}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className={labelClass}>
                Adjustment type <span className="text-[rgb(var(--color-danger))]">*</span>
                <select className={inputClass} value={adjForm.adjustment_type}
                  onChange={(e) => updateAdjForm('adjustment_type', e.target.value)}>
                  {ADJUSTMENT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </label>
              <label className={labelClass}>
                Delta amount <span className="text-[rgb(var(--color-danger))]">*</span>
                <div className="flex gap-2">
                  <input type="number" step="0.01" className={inputClass}
                    value={adjForm.calculated_delta}
                    onChange={(e) => updateAdjForm('calculated_delta', e.target.value)}
                    placeholder="e.g. 4250.00 or -1200.00" />
                  <select className={inputClass + ' !mt-0'} style={{ maxWidth: '100px' }}
                    value={adjForm.adjustment_currency}
                    onChange={(e) => updateAdjForm('adjustment_currency', e.target.value)}>
                    {CURRENCY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <span className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                  Positive = rep is owed more · Negative = rep overpaid (rare)
                </span>
              </label>
              <label className={labelClass + ' sm:col-span-2'}>
                Adjustment reason <span className="text-[rgb(var(--color-danger))]">*</span>
                <textarea className={inputClass + ' min-h-24 resize-y'} rows={3}
                  value={adjForm.adjustment_reason}
                  onChange={(e) => updateAdjForm('adjustment_reason', e.target.value)}
                  placeholder="Explain why this correction is needed, referencing supporting documentation." />
              </label>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" className={btnPrimary} onClick={updateAdjustment} disabled={adjSaving}>
                {adjSaving ? 'Saving…' : 'Save changes'}
              </button>
              <button type="button" className={btnSecondary}
                onClick={() => { setShowAdjForm(false); resetAdjForm(); }} disabled={adjSaving}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* ── Workflow action panel ── */}
        {!isTerminal && !detailLoading && (
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Workflow actions</h3>

            {/* Status timeline */}
            <div className="flex items-center gap-3 text-tok-xs flex-wrap">
              {['draft', 'pending_approval', 'approved', 'applied'].map((s, i, arr) => (
                <React.Fragment key={s}>
                  <span className={`font-medium ${s === status ? 'text-[rgb(var(--color-primary))]' : 'text-[rgb(var(--color-text-muted))]'}`}>
                    {STATUS_META[s]?.label}
                  </span>
                  {i < arr.length - 1 && (
                    <span className="text-[rgb(var(--color-text-muted))]">→</span>
                  )}
                </React.Fragment>
              ))}
            </div>

            {isDraft && (
              <div className="flex items-center gap-2 flex-wrap">
                <button type="button" className={btnPrimary} onClick={submitAdj} disabled={actionLoading}>
                  {actionLoading ? 'Submitting…' : 'Submit for approval'}
                </button>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                  A different user must approve after submission.
                </p>
              </div>
            )}

            {isPending && (
              <div className="flex flex-col gap-3">
                {isSelfSubmitter ? (
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                    You submitted this adjustment. A different authorized user must review and approve or reject it.
                  </p>
                ) : (
                  <>
                    <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                      Review the adjustment reason and source payout context before approving.
                      Approval will queue this adjustment for the next calculation run.
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button type="button" className={btnSuccess} onClick={approveAdj} disabled={actionLoading}>
                        {actionLoading && !showRejectForm ? 'Approving…' : 'Approve'}
                      </button>
                      {!showRejectForm && (
                        <button type="button" className={btnDanger}
                          onClick={() => { setShowRejectForm(true); setRejectionError(''); }}
                          disabled={actionLoading}>
                          Reject
                        </button>
                      )}
                    </div>
                  </>
                )}

                {/* Rejection form — inline */}
                {showRejectForm && !isSelfSubmitter && (
                  <div className="rounded-lg border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/5 p-4 flex flex-col gap-3">
                    <h4 className="text-tok-sm font-semibold text-[rgb(var(--color-danger))]">Reject adjustment</h4>
                    {rejectionError && (
                      <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
                        {rejectionError}
                      </div>
                    )}
                    <label className={labelClass}>
                      Rejection reason <span className="text-[rgb(var(--color-danger))]">*</span>
                      <textarea className={inputClass + ' min-h-16 resize-y'} rows={2}
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Explain why this adjustment is being rejected." />
                    </label>
                    <div className="flex items-center gap-2">
                      <button type="button" className={btnDanger} onClick={rejectAdj} disabled={actionLoading}>
                        {actionLoading ? 'Rejecting…' : 'Confirm rejection'}
                      </button>
                      <button type="button" className={btnSecondary}
                        onClick={() => { setShowRejectForm(false); setRejectionReason(''); setRejectionError(''); }}
                        disabled={actionLoading}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {isApproved && (
              <div className="rounded-lg bg-[rgb(var(--color-success))]/10 border border-[rgb(var(--color-success))]/30 px-4 py-3 text-tok-sm">
                <p className="font-medium text-[rgb(var(--color-success))]">Approved — awaiting next calculation run</p>
                <p className="mt-1 text-[rgb(var(--color-text-muted))]">
                  This adjustment will be applied automatically when the next calculation run executes
                  for period {period?.period_code || selectedAdj.current_fiscal_period_id}.
                  Approved by {selectedAdj.approved_by === currentUserId ? 'you' : selectedAdj.approved_by}
                  {selectedAdj.approved_at ? ` on ${fmtDate(selectedAdj.approved_at)}` : ''}.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Applied / rejected terminal state */}
        {isTerminal && (
          <div className={`rounded-xl border p-5 shadow-sm ${
            status === 'applied'
              ? 'bg-[rgb(var(--color-primary))]/5 border-[rgb(var(--color-primary))]/30'
              : 'bg-[rgb(var(--color-danger))]/5 border-[rgb(var(--color-danger))]/30'
          }`}>
            <p className={`text-tok-sm font-semibold ${
              status === 'applied' ? 'text-[rgb(var(--color-primary))]' : 'text-[rgb(var(--color-danger))]'
            }`}>
              {status === 'applied' ? '✓ Applied' : '✗ Rejected'}
            </p>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
              {status === 'applied'
                ? `Applied in run ${selectedAdj.applied_in_run_id} on ${fmtDate(selectedAdj.applied_at)}. This record is permanent.`
                : `Rejected on ${fmtDate(selectedAdj.rejected_at)}. Create a new adjustment if a correction is still required.`}
            </p>
          </div>
        )}
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // List view
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-6">
      {toast && (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      <header>
        {mode === 'wizard' && (
          <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-text-muted))] mb-1">
            Stage 8 · P3-05 · Enterprise
          </p>
        )}
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">True-Up Adjustments</h2>
        <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Retroactively correct compensation for prior periods. Adjustments are additive —
          they write new payout rows in the current period and never modify prior records.
          Each adjustment requires approval before the engine applies it.
        </p>
      </header>

      {/* Summary banner when pending items exist */}
      {pendingCount > 0 && (
        <div className="rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-4 py-3 text-tok-sm">
          <span className="font-medium text-[rgb(var(--color-warning))]">
            {pendingCount} adjustment{pendingCount !== 1 ? 's' : ''} pending approval
          </span>
          <span className="text-[rgb(var(--color-text-muted))]">
            {' '}— must be approved or rejected before the final run.
          </span>
        </div>
      )}

      {/* Filters + create button */}
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-4 flex items-end gap-3 flex-wrap shadow-sm">
        <label className={labelClass} style={{ minWidth: '180px' }}>
          Status
          <select className={inputClass} value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}>
            {ADJUSTMENT_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </label>
        <label className={labelClass} style={{ flex: '1', minWidth: '200px' }}>
          Position ID (optional)
          <input type="text" className={inputClass} value={filterPositionId}
            onChange={(e) => setFilterPositionId(e.target.value)}
            placeholder="Filter by position UUID" />
        </label>
        <button type="button" className={btnSecondary} onClick={loadAdjustments} disabled={listLoading}>
          {listLoading ? 'Loading…' : 'Search'}
        </button>
        {!showAdjForm && (
          <button type="button" className={btnPrimary}
            onClick={() => { resetAdjForm(); setShowAdjForm(true); }}>
            + New adjustment
          </button>
        )}
      </div>

      {/* Create form */}
      {showAdjForm && !editingAdjId && (
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">New true-up adjustment</h3>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            Reference a completed final-run payout summary from a prior period. The adjustment delta
            is added to the rep's current-period payout when approved and the engine runs.
          </p>
          {adjFormError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {adjFormError}
            </div>
          )}

          {/* Source payout lookup */}
          <div className="rounded-lg bg-[rgb(var(--color-surface-muted))]/60 border border-[rgb(var(--color-border))] p-4 flex flex-col gap-3">
            <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">Source payout summary</p>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              Enter the payout summary ID from the prior period being corrected. Must be a final run from a different (closed) period.
            </p>
            {sourceLookupError && (
              <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
                {sourceLookupError}
              </div>
            )}
            <div className="flex items-end gap-2">
              <label className={labelClass} style={{ flex: 1 }}>
                Payout summary ID <span className="text-[rgb(var(--color-danger))]">*</span>
                <input type="text" className={inputClass}
                  value={sourceLookupId}
                  onChange={(e) => { setSourceLookupId(e.target.value); setSourceLookupData(null); }}
                  placeholder="UUID of the prior-period final payout_summary" />
              </label>
              <button type="button" className={btnSecondary}
                onClick={lookupSourcePayout} disabled={sourceLookupLoading}>
                {sourceLookupLoading ? 'Looking up…' : 'Look up'}
              </button>
            </div>
            {sourceLookupData && (
              <div className="rounded-lg bg-[rgb(var(--color-success))]/10 border border-[rgb(var(--color-success))]/30 px-3 py-2 text-tok-sm">
                <p className="font-medium text-[rgb(var(--color-success))]">Source payout found</p>
                <p className="mt-1 text-[rgb(var(--color-text-muted))]">
                  Total payout:{' '}
                  {sourceLookupData.total_payout != null
                    ? `${Number(sourceLookupData.total_payout).toLocaleString(undefined, { minimumFractionDigits: 2 })} ${sourceLookupData.payout_currency_code || ''}`
                    : '—'}
                  {' '}· Run type: {sourceLookupData.run_type || '—'}
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className={labelClass}>
              Position <span className="text-[rgb(var(--color-danger))]">*</span>
              <select className={inputClass} value={adjForm.position_id}
                onChange={(e) => updateAdjForm('position_id', e.target.value)}>
                <option value="">Select a position…</option>
                {positions.map((p) => (
                  <option key={p.position_id} value={p.position_id}>
                    {p.full_name || p.position_id}
                  </option>
                ))}
              </select>
            </label>
            <label className={labelClass}>
              Current fiscal period ID <span className="text-[rgb(var(--color-danger))]">*</span>
              <input type="number" min="1" step="1" className={inputClass}
                value={adjForm.current_fiscal_period_id}
                onChange={(e) => updateAdjForm('current_fiscal_period_id', e.target.value)}
                placeholder={period?.fiscal_period_id || 'e.g. 43'} />
              <span className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                The open period where this adjustment will be applied.
              </span>
            </label>
            <label className={labelClass}>
              Adjustment type <span className="text-[rgb(var(--color-danger))]">*</span>
              <select className={inputClass} value={adjForm.adjustment_type}
                onChange={(e) => updateAdjForm('adjustment_type', e.target.value)}>
                {ADJUSTMENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label} — {t.hint}</option>
                ))}
              </select>
            </label>
            <label className={labelClass}>
              Delta amount <span className="text-[rgb(var(--color-danger))]">*</span>
              <div className="flex gap-2">
                <input type="number" step="0.01" className={inputClass}
                  value={adjForm.calculated_delta}
                  onChange={(e) => updateAdjForm('calculated_delta', e.target.value)}
                  placeholder="e.g. 4250.00 or -1200.00" />
                <select className={inputClass + ' !mt-0'} style={{ maxWidth: '100px' }}
                  value={adjForm.adjustment_currency}
                  onChange={(e) => updateAdjForm('adjustment_currency', e.target.value)}>
                  {CURRENCY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <span className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                Positive = owe rep more · Negative = rep overpaid (rare)
              </span>
            </label>
            <label className={labelClass + ' sm:col-span-2'}>
              Adjustment reason <span className="text-[rgb(var(--color-danger))]">*</span>
              <textarea className={inputClass + ' min-h-24 resize-y'} rows={3}
                value={adjForm.adjustment_reason}
                onChange={(e) => updateAdjForm('adjustment_reason', e.target.value)}
                placeholder="Explain why this correction is needed. Reference any supporting documentation, finance sign-offs, or system correction records." />
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" className={btnPrimary} onClick={createAdjustment} disabled={adjSaving}>
              {adjSaving ? 'Creating…' : 'Create adjustment'}
            </button>
            <button type="button" className={btnSecondary}
              onClick={() => { setShowAdjForm(false); resetAdjForm(); }} disabled={adjSaving}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Errors */}
      {listError && (
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {listError}
        </div>
      )}

      {/* Adjustments list */}
      {!listLoading && adjustments.length === 0 && (
        <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-5 py-8 text-center">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            No adjustments found{period?.period_code ? ` for ${period.period_code}` : ''}.
            Create a new adjustment to correct compensation for a prior period.
          </p>
        </div>
      )}

      {adjustments.length > 0 && (
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-sm overflow-x-auto">
          <table className="min-w-full text-tok-sm border-collapse">
            <thead>
              <tr>
                <th className={thClass}>Position</th>
                <th className={thClass}>Type</th>
                <th className={thClass}>Delta</th>
                <th className={thClass}>Currency</th>
                <th className={thClass}>Current period</th>
                <th className={thClass}>Status</th>
                <th className={thClass}>Created</th>
                <th className={thClass} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {adjustments.map((adj) => (
                <tr key={adj.adjustment_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                  <td className={tdClass}>
                    {positionNameById[String(adj.position_id)] || (
                      <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{adj.position_id}</span>
                    )}
                  </td>
                  <td className={tdClass}>
                    {ADJUSTMENT_TYPES.find((t) => t.value === adj.adjustment_type)?.label || adj.adjustment_type}
                  </td>
                  <td className={tdClass + ` font-medium ${
                    Number(adj.calculated_delta) > 0
                      ? 'text-[rgb(var(--color-success))]'
                      : 'text-[rgb(var(--color-danger))]'
                  }`}>
                    {fmtMoney(adj.calculated_delta)}
                  </td>
                  <td className={tdClass}>{adj.adjustment_currency}</td>
                  <td className={tdClass}>{period?.period_code || `Period ${adj.current_fiscal_period_id}`}</td>
                  <td className={tdClass}><StatusBadge status={adj.adjustment_status} /></td>
                  <td className={tdClass}>{fmtDate(adj.created_at)}</td>
                  <td className={tdClass}>
                    <button type="button"
                      className="text-tok-sm text-[rgb(var(--color-primary))] hover:underline"
                      onClick={() => loadAdjDetail(adj.adjustment_id)}>
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
